import React, { useEffect, useRef, useState } from "react";
import { FormControl, TextField, Chip, Avatar, FormHelperText } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { InviteableUser, getInviteableUsers } from "api/bookings";
import i18n from "../i18n";

// prop types
interface ParticipantsPickerProps {
    coaches: string[],
    court: string,
    disabled?: boolean,
    error?: string | null,
    excludeUsers?: string[],
    label?: string,
    participants: InviteableUser[],
    onChange: (participants: InviteableUser[], coaches: string[]) => void,
}

// start date form element
const ParticipantsPicker = ({ coaches, court, disabled, error, excludeUsers, label, participants, onChange }: ParticipantsPickerProps) => {
    // ui state
    const [inviteableUsers, setInviteableUsers] = useState<InviteableUser[]>([]);
    const latestQuery = useRef("");

    // get inviteable users on mount, on change
    const fetchInviteableUsers = async (query: string) => {
        latestQuery.current = query;
        const fetched = (await getInviteableUsers({ courtId: court, inviteableOnly: true, query })).data;
        if (latestQuery.current === query) { // is result still relevant (if older request takes longer)?
            const sorted = fetched.sort((u) => (u.followingYou ? -1 : 1));
            setInviteableUsers(sorted.filter((u) => !excludeUsers?.includes(u.id)));
        }
    };
    useEffect(() => { fetchInviteableUsers(""); }, [court]); // eslint-disable-line

    // relevant errors
    const relevantErrors = ["bookingNeedsAtLeastOneInvitee"];
    const getHelperText = () => {
        if (error === "bookingNeedsAtLeastOneInvitee") return i18n.noParticipantsSelected;
        return "";
    };

    // on participant click -> toggle coach status
    const onToggleCoach = (userId: string) => {
        if (coaches.includes(userId)) {
            onChange(participants, coaches.filter((id) => id !== userId));
        } else {
            onChange(participants, [...coaches, userId]);
        }
    };

    // render
    return (
        <FormControl fullWidth variant="outlined">
            <Autocomplete
                disabled={disabled}
                filterSelectedOptions
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                getOptionSelected={(option, value) => option.id === value.id}
                groupBy={(option) => (option.followingYou ? i18n.followingMe : i18n.club)}
                multiple
                options={participants.concat(inviteableUsers)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        error={relevantErrors.includes(error!)}
                        label={label || i18n.participants}
                        placeholder={i18n.enterName}
                        variant="outlined"
                    />
                )}
                renderTags={(value, getTagProps) => value.map((option, index) => (
                    <Chip
                        avatar={<Avatar src={option.profilePictureUrl} />}
                        label={`${option.firstName} ${option.lastName}${coaches.includes(option.id) ? " (Coach)" : ""}`}
                        onClick={option.isInviteableAsCoach ? () => onToggleCoach(option.id) : undefined}
                        {...getTagProps({ index })}
                    />
                ))}
                value={participants}
                onChange={(e, v) => onChange(v, coaches)}
                onInputChange={(e, v, r) => (r === "input") && fetchInviteableUsers(v)}
            />
            <FormHelperText error={relevantErrors.includes(error!)}>{getHelperText()}</FormHelperText>
        </FormControl>
    );
};

export default ParticipantsPicker;
